import { useEffect, useMemo, useRef } from "react";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { activityApi } from "@/lib/api/activity";
import {
  ACTIVITY_POLL_MS,
  ACTIVITY_REFETCH_DEBOUNCE_MS,
  MEMBER_ACTIVITY_KEY,
  debounce,
  flattenPages,
} from "@/lib/activity";
import { RECONNECT_EVENT, usePortalEvent } from "@/lib/portal-socket";

const PAGE_SIZE = 50;

/** Refetch every member-activity query when the portal socket says the feed
 *  changed. The ping is bare (`{type: "activity_changed"}`) — REST stays the
 *  source of truth and re-applies the visibility rules. Debounced so an alarm
 *  walk-through (a burst of events) costs one refetch. Also catches up after a
 *  dropped socket reconnects. */
export function useActivityLiveRefresh(): void {
  const qc = useQueryClient();
  const debouncedRef = useRef<ReturnType<typeof debounce> | null>(null);
  if (!debouncedRef.current) {
    debouncedRef.current = debounce(
      () => void qc.invalidateQueries({ queryKey: MEMBER_ACTIVITY_KEY }),
      ACTIVITY_REFETCH_DEBOUNCE_MS,
    );
  }
  useEffect(() => () => debouncedRef.current?.cancel(), []);

  const ping = () => debouncedRef.current?.();
  usePortalEvent("activity_changed", ping);
  usePortalEvent(RECONNECT_EVENT, ping);
}

/** One day of the member's activity feed, paged by `next_cursor`. */
export function useMemberActivity(day: string) {
  useActivityLiveRefresh();

  const query = useInfiniteQuery({
    queryKey: [...MEMBER_ACTIVITY_KEY, "day", day],
    queryFn: ({ pageParam }) =>
      activityApi.list({ date: day, cursor: pageParam, limit: PAGE_SIZE }).then((r) => r.data),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    refetchInterval: ACTIVITY_POLL_MS,
    staleTime: 0,
  });

  const events = useMemo(() => flattenPages(query.data?.pages), [query.data]);

  // A page can come back short — even empty — with a cursor when the backend's
  // scan budget ran out. With nothing to show yet, keep asking instead of
  // flashing the empty state (or a lone "Load more") at the member.
  const { hasNextPage, isFetchingNextPage, isFetching, fetchNextPage } = query;
  useEffect(() => {
    if (events.length === 0 && hasNextPage && !isFetchingNextPage && !isFetching) {
      void fetchNextPage();
    }
  }, [events.length, hasNextPage, isFetchingNextPage, isFetching, fetchNextPage]);

  return { ...query, events };
}

/** The newest few events for the dashboard's "Recent activity" card. */
export function useRecentMemberActivity(limit = 20) {
  useActivityLiveRefresh();
  return useQuery({
    queryKey: [...MEMBER_ACTIVITY_KEY, "recent", limit],
    queryFn: () => activityApi.list({ limit }).then((r) => r.data.results),
    refetchInterval: ACTIVITY_POLL_MS,
    staleTime: 0,
  });
}
